// SitesPage.js
import React, { useState, useEffect, useRef, useLayoutEffect, useCallback } from 'react';
import { ConfigProvider, Card, Typography, Table, Button, Modal, Input, Tooltip, Space, message, List } from 'antd';
import { SearchOutlined, EditOutlined, SyncOutlined, LoadingOutlined, ApiOutlined } from '@ant-design/icons';

import { fetchSites, fetchSiteById, syncYetiSites, fetchPrecoroQBCustomers } from '../api';
import SideMenu from '../components/SideMenu';
import SiteEdit from '../components/SiteEdit';
import PrecoroQBCustomers from '../components/PrecoroQBCustomers';
import { renderSitesSummary } from '../components/YetiSync';
import { useQBCustomerOptions } from '../components/useQBCustomerOptions';

const SitesPage = () => {
  const [loading, setLoading] = useState(false);
  const [sites, setSites] = useState([]);
  const [filteredSites, setFilteredSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncOpen, setSyncOpen] = useState(false);
  const [syncSummary, setSyncSummary] = useState(null);
  const [qbCustomersOpen, setQbCustomersOpen] = useState(false);
  
  // QB customers state
  const [qbCustomersCA, setQbCustomersCA] = useState([]);
  const [qbCustomersUS, setQbCustomersUS] = useState([]);
  const [qbCustomersLoading, setQbCustomersLoading] = useState(false);

  // Build hierarchical options once at the top level
  const qbOptionsCA = useQBCustomerOptions(qbCustomersCA);
  const qbOptionsUS = useQBCustomerOptions(qbCustomersUS);

  const [searchQuery, setSearchQuery] = useState('');
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('site_id');

  const [messageApi, contextHolder] = message.useMessage();

  const loadSites = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchSites();
      setSites(data);
    } catch (err) {
      console.error("Error fetching sites:", err);
      messageApi.error("Error fetching sites");
    } finally {
      setLoading(false);
    }
  }, [messageApi]);

  useEffect(() => {
    loadSites();
  }, [loadSites]);

  // Load QB customers function
  const loadQBCustomers = useCallback(async () => {
    setQbCustomersLoading(true);
    try {
      const [caCustomers, usCustomers] = await Promise.all([
        fetchPrecoroQBCustomers('CA', true, ''),
        fetchPrecoroQBCustomers('US', true, '')
      ]);
      setQbCustomersCA(caCustomers);
      setQbCustomersUS(usCustomers);
    } catch (error) {
      console.error('Error loading QB customers:', error);
      messageApi.error('Failed to load QuickBooks customers');
    } finally {
      setQbCustomersLoading(false);
    }
  }, [messageApi]);

  // Load QB customers on mount
  useEffect(() => {
    loadQBCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only load once on mount; messageApi is stable

  useEffect(() => {
    const filteredData = sites.filter(site => {
      const siteName = site.site_name || 'N/A';
      const location = site.city || 'N/A';
      const client = site.client_name || 'N/A';
  
      const routes = site.routes_details
        ? site.routes_details.map((r) => r.route_name).join(', ').toLowerCase()
        : '';
      const zones = site.routes_details
        ? site.routes_details.map((r) => r.zone_name).join(', ').toLowerCase()
        : '';
  
      return (
        siteName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        location.toLowerCase().includes(searchQuery.toLowerCase()) ||
        client.toLowerCase().includes(searchQuery.toLowerCase()) ||
        routes.includes(searchQuery.toLowerCase()) ||
        zones.includes(searchQuery.toLowerCase())
      );
    });
  
    setFilteredSites(filteredData);
  }, [searchQuery, sites]);

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedSites = filteredSites.sort((a, b) => {
    const aValue = (a[orderBy] ?? 'N/A').toString();
    const bValue = (b[orderBy] ?? 'N/A').toString();
  
    return order === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
  });
  
  const handleSyncClose = () => {
    setSyncSummary(null);
    setSyncOpen(false);
  };

  const handleYetiSync = async () => {
    setSyncLoading(true);
    try {
      const response = await syncYetiSites();
      setSyncSummary(response);
    } catch (error) {
      console.error("Failed to sync Sites with Yeti:", error);
      messageApi.error("Failed to sync Sites with Yeti");
    } finally {
      setSyncLoading(false);
      // The sync saves as it goes, so reload even if it failed partway
      loadSites();
    }
  };

  const handleSiteSelect = async (siteId) => {
    try {
      const siteData = await fetchSiteById(siteId);
      setSelectedSite(siteData);
    } catch (error) {
      console.error('Error fetching site data:', error);
      messageApi.error("Error fetching site data");
    }
  };

  const handleEditContract = async (siteId) => {
    try {
      const siteData = await fetchSiteById(siteId);
      setSelectedSite(siteData);
      setEditOpen(true);
    } catch (error) {
      console.error('Error fetching site data:', error);
      messageApi.error("Error fetching site data");
    }
  };

  const handleSiteUpdate = (updatedSite) => {
    if (!updatedSite) {
      console.error('handleSiteUpdate called with undefined/null updatedSite');
      return;
    }
    
    // Update the selected site in the modal, preserving existing data
    setSelectedSite(prevSite => ({
      ...prevSite,
      ...updatedSite
    }));
    
    // Update the site in the sites list
    if (updatedSite.site_id) {
      setSites(prevSites => 
        prevSites.map(site => 
          site.site_id === updatedSite.site_id ? { ...site, ...updatedSite } : site
        )
      );
    }
  };

  const handleEditClose = () => {
    setSelectedSite(null);
    setEditOpen(false);
  };

  const handleExpand = (expanded, site) => {
    if (expanded) {
      setExpandedRowKeys([...expandedRowKeys, site.site_id]);
    } else {
      setExpandedRowKeys(expandedRowKeys.filter(key => key !== site.site_id));
    }
  };

  const expandedRowRender = (site) => {
    if (!site.routes_details || site.routes_details.length === 0) {
      return (
        <div style={{ marginLeft: '64px' }}>
          <List.Item>
            <List.Item.Meta
              title='Site not assigned to any routes.'
            />
          </List.Item>
        </div>
      )
    }
  
    return (
      <div style={{ marginLeft: '64px' }}>
        <List
          dataSource={site.routes_details}
          renderItem={(r) => (
            <List.Item>
              <List.Item.Meta
                title={r.route_name}
              />
            </List.Item>
          )}
        />
      </div>
    );
  };

  const columns = [
    {
      title: 'Site Name',
      dataIndex: 'site_name',
      key: 'site_name',
      width: '35%',
      sorter: true,
      sortOrder: orderBy === 'site_name' && order,
      onHeaderCell: () => ({ onClick: () => handleRequestSort('site_name') }),
    },
    {
      title: 'Location',
      dataIndex: 'city',
      key: 'city',
      sorter: true,
      sortOrder: orderBy === 'city' && order,
      onHeaderCell: () => ({ onClick: () => handleRequestSort('city') }),
    },
    {
      title: 'Reference',
      dataIndex: 'site_reference',
      key: 'site_reference',
      sorter: true,
      sortOrder: orderBy === 'site_reference' && order,
      onHeaderCell: () => ({ onClick: () => handleRequestSort('site_reference') }),
    },
    {
      title: 'Client',
      dataIndex: 'client_name',
      key: 'client_name',
      sorter: true,
      sortOrder: orderBy === 'client_name' && order,
      onHeaderCell: () => ({ onClick: () => handleRequestSort('client_name') }),
    },
    {
      title: 'Actions',
      key: 'actions',
      align: 'center',
      width: '10%',
      render: (_, record) => (
        <EditOutlined
          onClick={(e) => {
            e.stopPropagation();
            handleEditContract(record.site_id);
          }}
        />
      ),
    },
  ];
    
  const [tableHeight, setTableHeight] = useState('100vh - 72px - 16px');
  const titleRef = useRef(null);
  const actionsRef = useRef(null);
  const tableRef = useRef(null);

  useLayoutEffect(() => {
    const calculateTableHeight = () => {
      const headerHeight = 72;
      const titleHeight = titleRef.current ? titleRef.current.offsetHeight : 48;
      const actionsHeight = actionsRef.current ? actionsRef.current.offsetHeight : 32;
      const padding = 16;

      // Calculate height of the table header
      const tableHeaderHeight = tableRef.current ? tableRef.current.querySelector('.ant-table-thead').offsetHeight : 55;

      const totalHeightAroundTableRows =
        headerHeight +
        titleHeight +
        actionsHeight +
        padding * 4 +
        tableHeaderHeight;
      console.log(totalHeightAroundTableRows);
      setTableHeight(`calc(100vh - ${totalHeightAroundTableRows}px)`);
    };

    calculateTableHeight();

    window.addEventListener('resize', calculateTableHeight);
    return () => window.removeEventListener('resize', calculateTableHeight);
  }, []);

  return (
    <ConfigProvider theme={{ token: { colorPrimary: '#F57D20' } }}>
      {contextHolder}
      <div style={{ display: 'flex' }}>
        <SideMenu />
        
        <Card style={{ height: 'calc(100vh - 72px - 16px)', marginRight: '16px', overflow: 'hidden', divShadow: '0 0 10px rgba(0, 0, 0, 0.1)' }}>
          <Typography.Title ref={titleRef} level={2} style={{ margin: 0, paddingBottom: '10px' }}>
            Sites
          </Typography.Title>
          
          <div ref={actionsRef} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <Input
              placeholder="Search Sites"
              value={searchQuery}
              onChange={handleSearchChange}
              style={{ width: '300px' }}
              prefix={<SearchOutlined style={{ color: '#d9d9d9' }} />}
            />
            <div style={{ display: 'flex', gap: '8px' }}>
              <Tooltip title="Map Sites to QuickBooks Customers">
                <Button
                  onClick={() => setQbCustomersOpen(true)}
                >
                  QuickBooks Customer Mapping <ApiOutlined />
                </Button>
              </Tooltip>
              <Tooltip title="Sync Sites with Yeti">
                <Button
                  shape="circle"
                  icon={<SyncOutlined />}
                  onClick={() => setSyncOpen(true)}
                  disabled={syncLoading}
                />
              </Tooltip>
            </div>
          </div>
            
          <div ref={tableRef} style={{ overflow: 'hidden', marginTop: '16px' }}>
            <Table
              columns={columns}
              dataSource={sortedSites}
              pagination={false}
              onRow={(record) => ({
                onClick: () => handleSiteSelect(record.site_id),
                style: { cursor: 'pointer' },
              })}
              expandable={{
                expandedRowKeys,
                onExpand: handleExpand,
                expandedRowRender: expandedRowRender,
              }}
              scroll={{ y: tableHeight }}
              sticky
              loading={loading}
            />
          </div>
        </Card>
      </div>
      
      <Modal
        open={editOpen}
        onClose={handleEditClose}
        onCancel={handleEditClose}
        centered
        width='50%'
        footer={[
          <Button
            key="submit"
            type="primary"
            onClick={handleEditClose}
          >
            Close
          </Button>,
        ]}
      >
        <SiteEdit
          site={selectedSite}
          messageApi={messageApi}
          qbOptionsCA={qbOptionsCA}
          qbOptionsUS={qbOptionsUS}
          qbCustomersLoading={qbCustomersLoading}
          onSiteUpdate={handleSiteUpdate}
        />
      </Modal>

      <Modal
        title={syncSummary ? "Yeti Site Sync Summary" : "Sync Sites with Yeti"}
        open={syncOpen}
        onCancel={handleSyncClose}
        footer={[
          <Button key="close" onClick={handleSyncClose} disabled={syncLoading}>Close</Button>,
          !syncSummary && <Button key="start" type="primary" onClick={handleYetiSync} disabled={syncLoading}>Start Site Sync</Button>
        ]}
      >
        {syncLoading ? (
          <Space direction="horizontal" size="middle">
            <Typography variant='text'>Syncing Sites</Typography>
            <LoadingOutlined style={{ fontSize: '32px' }} spin />
          </Space>
        ) : syncSummary ? (
          <div>
            <br/>
            {renderSitesSummary(syncSummary)}
          </div>
        ) : (
          <Typography variant='text'>Syncing Sites with Yeti will add new Sites and update existing Site records.</Typography>
        )}
      </Modal>

      <PrecoroQBCustomers
        open={qbCustomersOpen}
        onClose={() => setQbCustomersOpen(false)}
        sites={sites}
        onSiteUpdate={handleSiteUpdate}
        onQBCustomersSync={loadQBCustomers}
        messageApi={messageApi}
        qbOptionsCA={qbOptionsCA}
        qbOptionsUS={qbOptionsUS}
      />
    </ConfigProvider>
  );
};

export default SitesPage;